import { useCallback, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  GetAssemblyPointOfInterestsQuery,
  useGetAssemblyPointOfInterestsQuery,
  useGetRecommendationsForAssemblyQuery,
} from '@/__generated__/graphql';
import { DetailsAccordion } from '../StyledComponents';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as state from '@/components/Analysis/state';
import { useHavePermission } from '@/hooks';
import { Permissions } from '@/types';
import { PointOfInterestDocument } from '@/types';
import { NominateDialog } from '@/components/Analysis/modules/pointOfInterest/aitInsights/Blisters/NominateDialog';
import AllowedNominationTable from './AllowedNominationTable';

type TableColumn = {
  key?: string | null;
  displayName?: string | null;
};
type NominationConfig = {
  title?: string | null;
  columns?: Array<TableColumn | null> | null;
};
type Props = {
  assemblyId: string | undefined;
  poiTemplateNominationTypes?: Array<string | null> | null | undefined;
  nominationTableConfig?: NominationConfig | null | undefined;
  hasConditionGradeField: boolean | undefined;
};

export const AllowedNomination = ({
  assemblyId,
  poiTemplateNominationTypes,
  nominationTableConfig,
  hasConditionGradeField,
}: Props) => {
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const structureId = useGetStructureId();
  const unitSystem = useAtomValue(state.unitSystem);

  const { data: assemblyPoisData } = useGetAssemblyPointOfInterestsQuery({
    variables: {
      input: {
        assemblyIds: assemblyId ? [assemblyId] : [],
        assemblyTemplateId: selectedAssemblyTemplateId || '',
        structureId,
      },
    },
    fetchPolicy: 'no-cache',
    skip: !assemblyId || !selectedAssemblyTemplateId,
  });

  const allowedPoiTypes = useMemo(() => {
    if (!hasConditionGradeField) return [] as string[];
    const defaults = ['PIT', 'BLISTER'];
    const provided = (poiTemplateNominationTypes ?? []).map((type) =>
      type ? type.toUpperCase() : type
    );
    return Array.from(new Set([...(provided.filter(Boolean) as string[]), ...defaults]));
  }, [hasConditionGradeField, poiTemplateNominationTypes]);

  const { data } = useGetRecommendationsForAssemblyQuery({
    variables: {
      assemblyId: assemblyId!,
    },
    fetchPolicy: 'network-only',
    skip: !assemblyId,
  });

  const userCanUpdateAsset = useHavePermission(Permissions.UPDATE_ASSET);
  const governingPoiId = data?.assembly?.recommendation?.governingValue?.pointOfInterest?.id;

  const [poiIdToNominate, setPoiIdToNominate] = useState<string | undefined>();
  const [isNominateOpen, setIsNominateOpen] = useState(false);

  const allowedNominationPois = useMemo(() => {
    if (!hasConditionGradeField)
      return [] as GetAssemblyPointOfInterestsQuery['allPointOfInterests'];
    const allPois = assemblyPoisData?.allPointOfInterests ?? [];
    return allPois.filter((poi) => allowedPoiTypes?.includes(poi.type));
  }, [assemblyPoisData, hasConditionGradeField, allowedPoiTypes]);

  const handleNominate = useCallback((poi: PointOfInterestDocument) => {
    setPoiIdToNominate(poi.id);
    setIsNominateOpen(true);
  }, []);

  const handleCloseNominateDialog = useCallback(() => setIsNominateOpen(false), []);
  const title = nominationTableConfig?.title || 'Allowed POIs for Nomination';

  return (
    <>
      <DetailsAccordion title={title} subTitle={allowedNominationPois.length || ''}>
        <AllowedNominationTable
          allowedNominationPois={allowedNominationPois}
          unitSystem={unitSystem}
          governingPoiId={governingPoiId}
          userCanUpdateAsset={userCanUpdateAsset}
          handleNominate={handleNominate}
          tableColumns={nominationTableConfig?.columns ?? []}
        />
      </DetailsAccordion>

      {isNominateOpen && assemblyId && data?.assembly?.tagName && poiIdToNominate && (
        <NominateDialog
          isOpen={isNominateOpen}
          onClose={handleCloseNominateDialog}
          assemblyId={assemblyId}
          assemblyTagName={data?.assembly.tagName}
          blisterId={poiIdToNominate || ''}
          pipeSpec={data?.assembly?.pipeSpec}
        />
      )}
    </>
  );
};
